import { FC, useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetActivityQuery, useUpdateActivityMutation } from '../../graphql/generated/operations/activity.ts';
import { ActivityInput } from '../../graphql/generated/graphql.ts';
import { CancelAndPlan } from './steps/cancel-future-plans.tsx';
import { SubGoalSelection } from '../activity-planning/steps/sub-goal-selection.tsx';
import { TimeSelection } from '../activity-planning/steps/time-selection.tsx';
import { ActivityView } from '../../components/activity/activity-view.tsx';
import { Paths } from '../../constants/paths.ts';
import { trackEvent } from '../../utils/analytics.ts';

export const ActivityEditingPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [input, setInput] = useState<Partial<ActivityInput>>({});
  const [updateActivity] = useUpdateActivityMutation();
  const { data: existingActivityData } = useGetActivityQuery({
    variables: { id: id ?? '' },
    skip: !id
  });

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
    if (isExpanded) {
      trackEvent('activity_edit_section_open', { section: panel });
    }
  };

  useEffect(() => {
    if (existingActivityData?.activity) {
      const activity = existingActivityData.activity;
      setInput({
        description: activity.description,
        type: activity.type,
        subgoal: activity.subgoal,
        schedule: activity.schedule.map((item) => {
          const { __typename, ...rest } = item;
          return rest;
        }),
        domainID: activity.domainID
      });
    }
  }, [existingActivityData]);

  return (
    <>
      <ActivityView activity={existingActivityData?.activity} />
      <Stack sx={{ mt: 3 }}>
        <Accordion expanded={expanded === 'subgoal'} onChange={handleChange('subgoal')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Update Subgoal</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SubGoalSelection
              flat
              domain={existingActivityData?.activity.domain ?? {}}
              input={input}
              onNext={(newInput) => {
                setInput(newInput);
                trackEvent('activity_update', { type: 'subgoal', activityId: id });
                updateActivity({
                  variables: {
                    id: id ?? '',
                    input: newInput as ActivityInput
                  }
                });
                navigate(0);
              }}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'times'} onChange={handleChange('times')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Update Times</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TimeSelection
              flat
              domain={existingActivityData?.activity.domain ?? {}}
              input={input}
              onNext={(newInput) => {
                setInput(newInput);
                trackEvent('activity_update', { type: 'times', activityId: id });
                updateActivity({
                  variables: {
                    id: id ?? '',
                    input: newInput as ActivityInput
                  }
                });
                navigate(0);
              }}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'cancel'} onChange={handleChange('cancel')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Cancel & Plan New One</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CancelAndPlan
              input={input}
              onNext={(newInput) => {
                setInput(newInput);
                trackEvent('activity_update', { type: 'cancel_and_plan', activityId: id });
                updateActivity({
                  variables: {
                    id: id ?? '',
                    input: newInput as ActivityInput
                  }
                });
                navigate(Paths.ACTIVITY_PLANNING);
              }}
            />
          </AccordionDetails>
        </Accordion>
      </Stack>
    </>
  );
};
